<template>
  <section>
    <el-form
      ref="paylinkForm"
      :model="paylinkForm"
      :rules="paylinkFormRules"
      label-width="150px"
    >
      <el-form-item
        label="Store"
        prop="store">
        <gx-select
          v-model="paylinkForm.store"
          :show-label="false"
          :options="stores"
          track-by="gx_deeplink_advertiser_id"
          label="name"
        >
          <template #selectedLabel="{ option }">{{ option.name }}</template>
          <template #option="{ option }">
            <div class="d-flex justify-content-space-between">
              <span>{{ option.name }} ({{ option.gx_deeplink_advertiser_id }})</span>
              <span style="color: #F56C6C">{{ option.gx_deeplink_affiliate }}</span>
            </div>
          </template>
        </gx-select>
      </el-form-item>
      <el-form-item
        label="Country"
        prop="country">
        <gx-select
          v-model="paylinkForm.country"
          :show-label="false"
          :options="countries"
          track-by="value"
          label="name"
        >
          <template #selectedLabel="{ option }">{{ option.name }}</template>
          <template #option="{ option }">
            <div class="d-flex justify-content-space-between">
              <span>{{ option.name }}</span>
              <span>{{ option.value }}</span>
            </div>
          </template>
        </gx-select>
      </el-form-item>
      <el-form-item prop="targetUrl">
        <span slot="label">
          Target URL(s) <gx-tooltip>Please separate urls by creating new line<br>Example:<br>www.google.com<br>www.goxip.com</gx-tooltip>
        </span>
        <el-input
          :autosize="{ minRows: 3, maxRows: 6 }"
          v-model="paylinkForm.targetUrl"
          type="textarea"/>
      </el-form-item>
      <el-form-item prop="identifier">
        <span slot="label">
          Identifier (U1)
        </span>
        <el-input v-model.trim="paylinkForm.identifier"/>
      </el-form-item>
      <el-form-item prop="pageTitle">
        <span slot="label">
          Page Title (Optional)
        </span>
        <el-input v-model.trim="paylinkForm.pageTitle"/>
      </el-form-item>
      <el-form-item prop="bannerImage">
        <span slot="label">
          Banner Image (Optional)
        </span>
        <el-input v-model.trim="paylinkForm.bannerImage"/>
      </el-form-item>
      <div class="button-group">
        <el-button @click="handleClear">{{ $t('button.clear') }}</el-button>
        <el-button
          type="primary"
          @click="handleGenerate">{{ $t('button.generate') }}</el-button>
      </div>
    </el-form>
    <div
      v-show="generatedPaylink"
      class="paylink-result-container">
      <h3>Generated Paylink(s)</h3>
      <el-input
        v-model="generatedPaylink"
        type="textarea"
        autosize
        readonly/>

      <div class="button-group">  
        <el-button
          v-clipboard:copy="generatedPaylink"
          v-clipboard:success="copySuccessed"
          type="primary"
        >
          {{ copyText }}
        </el-button>
        <el-button
          @click="handleGenerateShortUrl"
        >
          {{ generateShortUrlText }}
        </el-button>
      </div>
    </div>
    <div
      v-show="generatedShortUrl"
      class="paylink-result-container">
      <h3>Generated Short URL</h3>
      <el-input
        v-model="generatedShortUrl"
        type="textarea"
        autosize
        readonly/>
      <el-button
        v-clipboard:copy="generatedShortUrl"
        v-clipboard:success="copySuccessed"
        type="primary"
      >
        {{ copyText }}
      </el-button>
    </div>
  </section>
</template>

<script>
import clipboard from '../../directive/clipboard'
import { getAffiliateStoresWithPaging } from '../../api/stores'
import { createLinkMeta } from '../../api/paylinks'
import RedirectLinkService from '../../api/redirect-link'
import { GOXIP_PAYLINK_DOMAIN, GOXIP_PAYLINK_DOMAIN_TH, countryList } from '../../utils/const'

export default {
  name: 'PaylinkGenerator',
  directives: {
    clipboard,
  },
  data() {
    const checkUrl = (_, value, callback) => {
      if(!value) {
        return callback(new Error(this.$t('error.thisFieldIsRequired')))
      }
      // const regex = /^(?:http(s)?:\/\/)/
      // const isValidUrl = regex.test(value)
      // if(!isValidUrl) {
      //   return callback(new Error(this.$t('Please enter valid url.')))
      // }

      const domains = value.split('\n')
      if (this.paylinkForm.store.validation_text) {
        const validationTexts = this.paylinkForm.store.validation_text.split(",")
        for (const domain of domains) {
          const matchWithStoreUrl = !!validationTexts.find(text => domain.toLowerCase().includes(text.toLowerCase()))
          if (!matchWithStoreUrl) {
            return callback(new Error('The entered URL doesn\'t match the advertiser you selected.'))
          }
          if (domain.toLowerCase().includes('utm') && this.paylinkForm.store.id !== 159) {
            return callback(new Error('There should be no utm at the URL'))
          }
        }
      }

      return callback()
    }
    return {
      paylinkForm: {
        store: '',
        country: '',
        targetUrl: '',
        identifier: '',
        pageTitle: '',
        bannerImage: '',
      },
      paylinkFormRules: {
        store: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'change' }],
        country: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'change' }],
        targetUrl: [{ required: true, validator: checkUrl, trigger: 'blur' }], 
        identifier: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }],
      },
      stores: [],
      countries: countryList,
      generatedPaylink: '',
      copyText: 'Copy',
      generatedShortUrl: '',
      generateShortUrlText: 'Generate Short URL',
    }
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const response = await getAffiliateStoresWithPaging({ size: 9999, is_deeplink: 1 })
      next((vm) => { vm._data.stores = response.data.stores }) // eslint-disable-line no-underscore-dangle
    } catch (error) {
      next(new Error('Could not get store list'))
    }
  },
  methods: {
    generatePaylinks() {
      const urlType = 'affiliate'
      const countryCode = this.paylinkForm.country.value.toLowerCase()
      const url = countryCode === 'th' ? GOXIP_PAYLINK_DOMAIN_TH : `${GOXIP_PAYLINK_DOMAIN}/${countryCode}`
      const advertiserId = this.paylinkForm.store.gx_deeplink_advertiser_id
      const affiliate = this.paylinkForm.store.gx_deeplink_affiliate.toLowerCase()
      const u1 = `${countryCode}|||${this.paylinkForm.identifier}`
      const targetURLs = this.paylinkForm.targetUrl.split('\n')

      const paylinks = []

      for (let i = 0; i < targetURLs.length; i += 1) {
        const targetURL = encodeURIComponent(targetURLs[i])
        const paylink = `${url}/${urlType}?url=${targetURL}&u1=${u1}&affiliate=${affiliate}&advertiser_id=${advertiserId}`
        paylinks.push(paylink)
      }

      return paylinks
    },
    handleClear() {
      this.$refs.paylinkForm.resetFields()
      this.generatedPaylink = ''
    },
    handleGenerate() {
      this.$refs.paylinkForm.validate((valid) => {
        if (valid) {
          let paylinks = this.generatePaylinks()
          const pageTitle = this.paylinkForm.pageTitle
          const bannerImage = this.paylinkForm.bannerImage
          
          if (pageTitle.length > 0 || bannerImage.length > 0) {
            createLinkMeta(pageTitle, bannerImage).then((response) => {
              if (response.data && response.data.hash) {
                for (let i = 0; i < paylinks.length; i++) {
                  paylinks[i] = `${paylinks[i]}&hash=${response.data.hash}`
                }
              }

              this.generatedPaylink = paylinks.join('\n')
            })
          } else {
            this.generatedPaylink = paylinks.join('\n')
          }
        } else {
          return false
        }
        return undefined
      })
    },
    async handleGenerateShortUrl() {
      if (this.generatedPaylink) {
        try {
          const data = await RedirectLinkService.create(this.generatedPaylink)
          const hash = data.data.hash

          this.generatedShortUrl = `${process.env.VUE_APP_GOXIP_BASE_URL}/l/${hash}`
        } finally {

        }
      }
    },
    copySuccessed() {
      this.copyText = 'Copied'
      setTimeout(() => {
        this.copyText = 'Copy'
      }, 3000)
    },
  },
}
</script>

<style lang="scss" scoped>
section {
  width: 100%;
  max-width: 650px;
  border: $border-base;
  border-radius: $border-radius-base;
  margin: 20px auto;
  padding: 10px;

  button {
    margin: 5px auto;
    width: 40%;
  }

  .el-form {
    .el-select {
      width: 100%;
    }
  }

  .paylink-result-container {
    display: flex;
    flex-flow: column;
    border-top: $border-base;
    margin-top: 10px;
    h3 {
      text-align: center;
    }
    button {
      margin-top: 15px;

    }
  }

  .button-group {
    display: flex;
  }
}
</style>
